import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import AudienceToggle from './AudienceToggle';
import { useAudience } from '../contexts/AudienceContext';
import { getBrandFallbackMarkSrc, getBrandLogoSrc } from '@/lib/brandConstants';

const Header = ({ onStartAssessment, onBookDemo }) => {
  const [logoSrc, setLogoSrc] = useState(getBrandLogoSrc);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isSchool } = useAudience();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const primaryCtaLabel = isSchool ? 'Book a school demo' : 'Know your Career DNA';
  const primaryAction = isSchool ? onBookDemo : onStartAssessment;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md transition-all duration-300">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex flex-nowrap items-center w-full gap-2 min-h-[4.25rem] py-2 lg:min-h-0 lg:h-20 lg:py-0">
          <Link
            to={isSchool ? '/schools' : '/'}
            className="flex shrink-0 items-center gap-1.5 py-1 pr-0 sm:gap-2 sm:pr-2"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <img
              src={logoSrc}
              alt="ChooseRightCareer logo"
              width={80}
              height={80}
              decoding="async"
              onError={() =>
                setLogoSrc((s) => {
                  const fb = getBrandFallbackMarkSrc();
                  return s === fb ? s : fb;
                })
              }
              className="h-10 w-10 sm:h-12 sm:w-12 lg:h-14 lg:w-14 xl:h-[4.5rem] xl:w-[4.5rem] object-contain rounded-full shrink-0"
            />
            <p className="hidden min-[340px]:block text-sm sm:text-lg lg:text-xl xl:text-2xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent m-0 whitespace-nowrap">
              ChooseRightCareer
            </p>
          </Link>

          <div className="hidden lg:flex flex-nowrap items-center gap-4 xl:gap-6 shrink-0 ml-auto">
            <nav className="flex flex-nowrap items-center gap-4 xl:gap-8">
              <button
                type="button"
                onClick={() => scrollToSection('hero')}
                className="font-medium text-gray-700 hover:text-blue-600 transition-colors duration-300"
              >
                Home
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('how-it-works')}
                className="font-medium text-gray-700 hover:text-blue-600 transition-colors duration-300"
              >
                How It Works
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('features')}
                className="font-medium text-gray-700 hover:text-blue-600 transition-colors duration-300"
              >
                Features
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('student-reviews')}
                className="font-medium text-gray-700 hover:text-blue-600 transition-colors duration-300"
              >
                Reviews
              </button>
            </nav>
            <AudienceToggle />
            <Button
              type="button"
              onClick={primaryAction}
              className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white transition-all duration-300 whitespace-nowrap"
            >
              {primaryCtaLabel}
            </Button>
          </div>

          <div className="ml-auto flex lg:hidden items-center gap-1.5 shrink-0">
            <AudienceToggle compact />
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="flex h-11 w-11 items-center justify-center rounded-xl text-gray-800 hover:bg-gray-100 transition-colors"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 py-4 shadow-lg">
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              <button
                type="button"
                onClick={() => scrollToSection('hero')}
                className="text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors duration-200"
              >
                Home
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('how-it-works')}
                className="text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors duration-200"
              >
                How It Works
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('features')}
                className="text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors duration-200"
              >
                Features
              </button>
              <button
                type="button"
                onClick={() => scrollToSection('student-reviews')}
                className="text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors duration-200"
              >
                Reviews
              </button>
              <Link
                to="/counsellors"
                className="text-left px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Expert counsellors
              </Link>
              <Button
                type="button"
                onClick={() => {
                  primaryAction();
                  setIsMobileMenuOpen(false);
                }}
                className="mx-4 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white"
              >
                {primaryCtaLabel}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
